import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { SearchRequestContent, SearchResponseContent } from "common/types";
import { CatalogItem, CatalogItemSimplified } from "common/types/catalog-types";
import { CatalogicalItem } from "common/types/catalogical-type";
import { getString } from "common/uistringlabels/uiStringUtils";
import { createSearchService } from "lib/services/i-search-service";
import React, { FC, useEffect, useRef, useState } from "react";

import { CatalogItemCard } from "../CatalogItemCard";
import { useMessageBannersBox } from "../MessageBannersBox/hook";

export interface LegacyRecommendedItemsProps {
  totalItems: number;
  perRow: number;
  tagToFilterBy?: string;
  useSmartRecommendations?: boolean;
  recommendedItemsData?: CatalogItemSimplified[] | CatalogItem[] | CatalogicalItem[] | null;
  isLoadingRecommendations?: boolean;
  recommendationsError?: Error | null;
  children?: React.ReactNode;
  trafficFrom: string;
}

const isCatalogicalItem = (item: any): item is CatalogicalItem => {
  return item && "sku" in item && "subtype" in item;
};

// Helper function to normalize item data for the card component
const normalizeItemForCard = (item: CatalogItemSimplified | CatalogItem | CatalogicalItem) => {
  if (isCatalogicalItem(item)) {
    // Map CatalogicalItem to the expected format
    return {
      name: item.name,
      shortDescription: item.shortDescription,
      logoImageUrl: item.logoUrl,
      itemId: item.taxonomyId,
    };
  } else {
    return {
      name: item.name,
      shortDescription: item.shortDescription,
      logoImageUrl: item.logoImageUrl,
      itemId: item.itemId,
    };
  }
};

const LegacyRecommendedItems: FC<LegacyRecommendedItemsProps> = ({
  totalItems,
  perRow,
  tagToFilterBy,
  useSmartRecommendations = false,
  recommendedItemsData,
  isLoadingRecommendations,
  recommendationsError,
  children,
  trafficFrom,
}) => {
  const { addErrorBanner } = useMessageBannersBox();
  const [legacyItemsData, setLegacyItemsData] = useState<
    CatalogItemSimplified[] | CatalogicalItem[] | CatalogItem[] | null
  >(null);
  // Use a ref to track if the component is mounted
  const isMounted = useRef(true);

  // Set isMounted to false when the component unmounts
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!useSmartRecommendations && !legacyItemsData) {
      fetchLegacyData();
    }
  }, [useSmartRecommendations]);

  const fetchLegacyData = async () => {
    try {
      // LEGACY BEHAVIOR
      const client = await createSearchService();
      const searchRequest: SearchRequestContent = {
        pageSize: totalItems,
        text: "",
        tags: tagToFilterBy ? [tagToFilterBy] : [],
      };
      const response: SearchResponseContent = await client.Search(searchRequest);
      if (!response.items) throw new Error("No item data found");

      // Only update state if the component is still mounted
      if (isMounted.current) {
        setLegacyItemsData(response.items);
      }
    } catch (error) {
      console.error("Error loading recommended items", error);
      // Only update state if the component is still mounted
      if (isMounted.current) {
        setLegacyItemsData([]);
        addErrorBanner(getString("errors.general"));
      }
    }
  };

  // Handle errors from smart recommendations
  useEffect(() => {
    if (recommendationsError && isMounted.current) {
      addErrorBanner(getString("errors.general"));
    }
  }, [recommendationsError, addErrorBanner]);

  // Determine which items data to use
  const itemsData = useSmartRecommendations ? recommendedItemsData : legacyItemsData;
  const isLoading = useSmartRecommendations ? isLoadingRecommendations : !legacyItemsData;

  const gridGap = 39;
  const items = perRow;

  return !isLoading && itemsData ? (
    <View>
      <Row
        id="recommended-items"
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        key={`${tagToFilterBy ?? "recommendations"}`}
        gridGap={`${gridGap}px`}
        rowGap="40px"
      >
        {itemsData.map((item, i: number) => {
          const normalizedItem = normalizeItemForCard(item);
          return (
            <Col
              key={i}
              style={{
                flex: `0 0 calc(${100 / items}% - ${gridGap}px)`,
                justifyContent: "start",
              }}
              data-cy={`recommended-item-${tagToFilterBy ?? "recommendations"}`}
            >
              <CatalogItemCard
                from={trafficFrom}
                name={normalizedItem.name}
                shortDescription={normalizedItem.shortDescription}
                logoUrl={normalizedItem.logoImageUrl}
                taxonomyId={normalizedItem.itemId}
              />
            </Col>
          );
        })}
      </Row>
      {children}
    </View>
  ) : (
    <View alignItems="center">
      <Spinner size={SpinnerSize.Small} />
    </View>
  );
};

export default LegacyRecommendedItems;
